import { inject } from '@angular/core';
import { Auth, authState } from '@angular/fire/auth';
import * as Sentry from '@sentry/angular';
import {
  combineLatest,
  distinctUntilChanged,
  map,
  Observable,
  of,
  switchMap,
} from 'rxjs';
import { AuthService } from './auth.service';
import { TenantContextService } from './tenant-context.service';

/**
 * Where the logged-in uid comes from.
 * - `authService`: apps that already bootstrap `AuthService` (admin, app). Uses
 *   its resolved `isLoggedIn$`/`uid$`, i.e. the session AFTER claim/tenant checks.
 * - `firebaseAuth`: apps that do NOT use `AuthService` (member-portal). Injecting
 *   it there would start its `onAuthStateChanged` side effects (it overwrites the
 *   URL-derived tenantId via `setFromClaims`), so the raw Firebase auth state is
 *   read instead.
 */
export type SentryUserSource = 'authService' | 'firebaseAuth';

interface SentryUserState {
  uid: string | null;
  tenantId: string | null;
  role: string | null;
}

/**
 * Keeps the Sentry user in sync with the auth state so every event says who
 * produced it. Only the Firebase uid is sent — never email, name, DNI or any
 * other personal data. Also sets the `tenantId` and `role` tags when known
 * (`role` is `superAdmin` for super admins, otherwise the primary role of the
 * active tenant from the `tenants` claim).
 *
 * Must be called once, in an injection context (e.g. the root component
 * constructor). If Sentry is not initialised (dev builds) the calls are no-ops.
 */
export function trackSentryUser(
  source: SentryUserSource = 'authService',
): void {
  const tenantCtx = inject(TenantContextService);

  let uid$: Observable<string | null>;
  if (source === 'authService') {
    const authService = inject(AuthService);
    uid$ = authService.isLoggedIn$.pipe(
      switchMap((loggedIn) => (loggedIn ? authService.uid$ : of(null))),
    );
  } else {
    uid$ = authState(inject(Auth)).pipe(map((user) => user?.uid ?? null));
  }

  // `activeRole$` already re-emits on every tenantId change (it combines the
  // tenantId and claims subjects), so the raw tenantId is read alongside it.
  const tags$ = combineLatest([
    tenantCtx.activeRole$,
    tenantCtx.isSuperAdmin$,
  ]).pipe(
    map(([role, isSuperAdmin]) => ({
      tenantId: tenantCtx.tenantIdOrNull,
      role: isSuperAdmin ? 'superAdmin' : (role ?? null),
    })),
  );

  uid$
    .pipe(
      switchMap(
        (uid): Observable<SentryUserState> =>
          tags$.pipe(
            map((tags) => ({
              uid,
              tenantId: tags.tenantId,
              // No session → no role (claims are cleared on logout anyway).
              role: uid ? tags.role : null,
            })),
          ),
      ),
      distinctUntilChanged(
        (a, b) =>
          a.uid === b.uid && a.tenantId === b.tenantId && a.role === b.role,
      ),
    )
    .subscribe((state) => {
      Sentry.setUser(state.uid ? { id: state.uid } : null);
      // The tenant tag is kept without a session when the app still knows the
      // tenant (mobile build tenant, member-portal URL tenant).
      Sentry.setTag('tenantId', state.tenantId ?? undefined);
      Sentry.setTag('role', state.role ?? undefined);
    });
}
